/**
 * Universal Swiper Carousel System
 * @version 3.0.2 - Added dynamic slides observer
 * 
 * @description Centralized swiper system for all site carousels.
 * Auto-initializes any element with '.swiper-carousel' class.
 * 
 * @features
 * - Auto-initialization on page load
 * - MutationObserver for dynamic content (e.g., product recommendations)
 * - Observer for dynamic slides (fixes fraction update)
 * - Five configuration types: universal, predictive-search, tabbed-collections, articles, and complementary-products
 * - Responsive breakpoints
 * - Free mode with momentum
 * - Scrollbar navigation (always visible)
 * - Header controls (buttons + fraction) for tabbed carousels
 * - Touch and mousewheel support
 * 
 * @dependencies Swiper.js library
 */

window.UniversalSwiper = (function() {
  
  // Universal swiper configuration (default)
  const UNIVERSAL_CONFIG = {
    slidesPerView: 1.5,
    spaceBetween: 16,
    freeMode: {
      enabled: true,
      momentum: true,
      momentumRatio: 0.8
    },
    mousewheel: {
      releaseOnEdges: true,
      forceToAxis: true,
      sensitivity: 1
    },
    touchEventsTarget: 'container',
    grabCursor: true,
    scrollbar: {
      el: '.swiper-scrollbar',
      draggable: true,
      dragSize: 'auto',
      hide: false
    },
    breakpoints: {
      0: {
        slidesPerView: 1.5,
        spaceBetween: 16
      },
      769: {
        slidesPerView: 2.5,
        spaceBetween: 20
      },
      1025: {
        slidesPerView: 3.5,
        spaceBetween: 20
      },
      1441: {
        slidesPerView: 4.5,
        spaceBetween: 20
      }
    }
  };

  // Complementary Products swiper configuration
  const COMPLEMENTARY_PRODUCTS_CONFIG = {
    slidesPerView: 1,
    spaceBetween: 0,
    touchEventsTarget: 'container',
    grabCursor: true,
    scrollbar: {
      el: '.swiper-scrollbar',
      draggable: true,
      dragSize: 'auto',
      hide: false
    },
    breakpoints: {
      0: {
        slidesPerView: 1,
        spaceBetween: 0
      }
    }
  };

  // Predictive search swiper configuration
  const PREDICTIVE_CONFIG = {
    slidesPerView: 1.5,
    spaceBetween: 0,
    slidesOffsetAfter: 20,
    freeMode: {
      enabled: true,
      momentum: true,
      momentumRatio: 0.8
    },
    mousewheel: {
      releaseOnEdges: true,
      forceToAxis: true,
      sensitivity: 1
    },
    touchEventsTarget: 'container',
    grabCursor: true,
    scrollbar: {
      el: '.swiper-scrollbar',
      draggable: true,
      dragSize: 'auto',
      hide: false
    },
    breakpoints: {
      0: { 
        slidesPerView: 1.5,
        spaceBetween: 0,
        slidesOffsetAfter: 20
      },
      421: { 
        slidesPerView: 1.5,
        spaceBetween: 20,
        slidesOffsetAfter: 20
      },
      769: { 
        slidesPerView: 2.5,
        spaceBetween: 20,
        slidesOffsetAfter: 20
      },
      1025: { 
        slidesPerView: 3.5,
        spaceBetween: 20,
        slidesOffsetAfter: 90
      },
      1440: { 
        slidesPerView: 3.5,
        spaceBetween: 20,
        slidesOffsetAfter: 90
      }
    }
  };

  // Tabbed Collections swiper configuration
  const TABBED_COLLECTIONS_CONFIG = {
    slidesPerView: 1.5,
    spaceBetween: 16,
    freeMode: {
      enabled: true,
      momentum: true,
      momentumRatio: 0.8
    },
    mousewheel: {
      releaseOnEdges: true,
      forceToAxis: true,
      sensitivity: 1
    },
    touchEventsTarget: 'container',
    grabCursor: true,
    scrollbar: {
      el: '.swiper-scrollbar',
      draggable: true,
      dragSize: 'auto',
      hide: false
    },
    breakpoints: {
      0: {
        slidesPerView: 1.5,
        spaceBetween: 16,
        slidesOffsetBefore: 20,
        slidesOffsetAfter: 20
      },
      769: {
        slidesPerView: 2.5,
        spaceBetween: 20,
        slidesOffsetBefore: 20,
        slidesOffsetAfter: 20
      },
      1025: {
        slidesPerView: 3.5,
        spaceBetween: 20,
        slidesOffsetBefore: 60,
        slidesOffsetAfter: 60
      },
      1441: {
        slidesPerView: 4.5,
        spaceBetween: 20,
        slidesOffsetBefore: 80,
        slidesOffsetAfter: 80
      }
    }
  };

  // Articles swiper configuration (3.5 max slides)
  const ARTICLES_CONFIG = {
    slidesPerView: 1.5,
    spaceBetween: 16,
    freeMode: {
      enabled: true,
      momentum: true,
      momentumRatio: 0.8
    },
    mousewheel: {
      releaseOnEdges: true,
      forceToAxis: true,
      sensitivity: 1
    },
    touchEventsTarget: 'container',
    grabCursor: true,
    scrollbar: {
      el: '.swiper-scrollbar',
      draggable: true,
      dragSize: 'auto',
      hide: false
    },
    breakpoints: {
      0: {
        slidesPerView: 1.5,
        spaceBetween: 16
      },
      769: {
        slidesPerView: 2.5,
        spaceBetween: 20
      },
      1025: {
        slidesPerView: 3.5,
        spaceBetween: 20
      },
      1441: {
        slidesPerView: 3.5,
        spaceBetween: 20
      }
    }
  };

  /**
   * Extract block ID from swiper element classes
   */
  function extractBlockId(swiperElement) {
    const classMatch = swiperElement.className.match(/(?:swiper-collection-|article-swiper-)(\S+)/);
    return classMatch ? classMatch[1] : null;
  }

  /**
   * Detect navigation buttons for header
   */
  function detectNavigationButtons(swiperElement) {
    const blockId = extractBlockId(swiperElement);
    
    if (blockId) {
      // Pattern 1: Block-specific buttons (tabbed collections/articles)
      const nextEl = `.slider-button-next-${blockId}`;
      const prevEl = `.slider-button-prev-${blockId}`;
      
      // Verify buttons exist
      if (document.querySelector(nextEl) && document.querySelector(prevEl)) {
        return { nextEl, prevEl };
      }
    }
    
    // Pattern 2: Buttons in swiper-controls-top
    const topNextButton = swiperElement.querySelector('.swiper-controls-top .swiper-button-next');
    const topPrevButton = swiperElement.querySelector('.swiper-controls-top .swiper-button-prev');
    
    if (topNextButton && topPrevButton) {
      return {
        nextEl: topNextButton,
        prevEl: topPrevButton
      };
    }
    
    // Pattern 3: Default buttons inside swiper
    const nextButton = swiperElement.querySelector('.swiper-button-next');
    const prevButton = swiperElement.querySelector('.swiper-button-prev');
    
    if (nextButton && prevButton) {
      return {
        nextEl: nextButton,
        prevEl: prevButton
      };
    }
    
    // No navigation buttons found
    return null;
  }

  /**
   * Update fraction pagination in header
   */
  function updateFractionPagination(swiper) {
    try {
      // Get real slides (no duplicates)
      const realSlides = Array.from(swiper.slides).filter(slide => 
        !slide.classList.contains('swiper-slide-duplicate')
      );
      const totalSlides = realSlides.length;
      
      // Get current slidesPerView from active breakpoint
      const slidesPerView = swiper.params.slidesPerView === 'auto' ? 1 : parseFloat(swiper.params.slidesPerView);
      
      // Calculate total "pages"
      let totalPages = Math.ceil(totalSlides - slidesPerView + 1);
      
      // If the result is 0 or negative, set to minimum of 1
      if (totalPages < 1) {
        totalPages = 1;
      }
      
      // Current position
      const currentPage = (swiper.snapIndex || swiper.activeIndex) + 1;
      
      const fractionText = currentPage + ' / ' + totalPages;
      
      // Update fraction in header with blockId
      const blockId = extractBlockId(swiper.el);
      if (blockId) {
        const fractionInHeader = document.querySelector(`.swiper-fraction-${blockId}`);
        if (fractionInHeader) {
          fractionInHeader.textContent = fractionText;
        }
      }
      
      // Update fraction in swiper-controls-top (for universal carousels)
      const fractionInControlsTop = swiper.el.querySelector('.swiper-controls-top .swiper-pagination-fraction');
      if (fractionInControlsTop) {
        fractionInControlsTop.textContent = fractionText;
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Observe changes in swiper-wrapper (for dynamic slides)
   */
  function observeSwiperSlides(swiperElement) {
    const swiperWrapper = swiperElement.querySelector('.swiper-wrapper');
    if (!swiperWrapper) return;

    const observer = new MutationObserver(() => {
      if (swiperElement.swiper) {
        // Update swiper when slides are added
        swiperElement.swiper.update();
        updateFractionPagination(swiperElement.swiper);
      }
    });

    observer.observe(swiperWrapper, {
      childList: true,
      subtree: false
    });

    return observer;
  }

  /**
   * Get configuration for a swiper element
   */
  function getConfig(swiperElement) {
    // Check swiper type
    const swiperType = swiperElement.dataset.swiperType;
    
    if (swiperType === 'complementary-products') {
      return Object.assign({}, COMPLEMENTARY_PRODUCTS_CONFIG);
    }
    
    if (swiperType === 'articles') {
      return Object.assign({}, ARTICLES_CONFIG);
    }
    
    if (swiperType === 'tabbed-collections') {
      return Object.assign({}, TABBED_COLLECTIONS_CONFIG);
    }
    
    if (swiperType === 'predictive-search') {
      return Object.assign({}, PREDICTIVE_CONFIG);
    }
    
    // Default to universal config
    return Object.assign({}, UNIVERSAL_CONFIG);
  }

  /**
   * Initialize a swiper
   */
  function initSwiper(swiperElement) {
    if (!swiperElement || typeof Swiper === 'undefined' || swiperElement.swiper) {
      return null;
    }

    try {
      const config = getConfig(swiperElement);
      
      // Detect navigation buttons in header (for tabbed collections/articles)
      const navigation = detectNavigationButtons(swiperElement);
      if (navigation) {
        config.navigation = navigation;
      }
      
      // Events for fraction update
      config.on = {
        init: function() {
          updateFractionPagination(this);
        },
        slideChange: function() {
          updateFractionPagination(this);
        }
      };

      const swiper = new Swiper(swiperElement, config);
      
      // Observe changes in swiper-wrapper for dynamic slides
      observeSwiperSlides(swiperElement);
      
      return swiper;
    } catch (error) {
      console.error('Error initializing swiper:', error);
      return null;
    }
  }

  /**
   * Initialize all swipers
   */
  function initAllSwipers() {
    const elements = document.querySelectorAll('.swiper-carousel:not(.swiper-initialized)');
    const initializedSwipers = [];
    
    elements.forEach(element => {
      const swiper = initSwiper(element);
      if (swiper) {
        initializedSwipers.push(swiper);
      }
    });
    
    return initializedSwipers;
  }

  /**
   * Observe DOM for dynamically added swipers
   */
  function observeDynamicSwipers() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          // Check if the added node is an element
          if (node.nodeType === 1) {
            // Check if the node itself is a swiper
            if (node.classList && node.classList.contains('swiper-carousel') && !node.swiper) {
              initSwiper(node);
            }
            
            // Check for swipers inside the added node
            const swipers = node.querySelectorAll && node.querySelectorAll('.swiper-carousel:not(.swiper-initialized)');
            if (swipers && swipers.length > 0) {
              swipers.forEach(swiper => initSwiper(swiper));
            }
          }
        });
      });
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return observer;
  }

  /**
   * Auto-initialization
   */
  function autoInit() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAllSwipers);
    } else {
      initAllSwipers();
    }
    
    // Backup initialization
    setTimeout(initAllSwipers, 100);
    setTimeout(initAllSwipers, 500);
    setTimeout(initAllSwipers, 1000);
    
    // Start observing for dynamic content
    observeDynamicSwipers();
  }

  // Public API
  return {
    init: initAllSwipers,
    autoInit: autoInit
  };
})();

// Auto-initialize
UniversalSwiper.autoInit();